/**
 * <citrus-carousel> — thin wrapper around Swiper (loaded globally as
 * swiper-bundle.min.js). Options are passed via data attributes so the
 * markup stays the single source of truth:
 *
 * data-slides-desktop  slides per view from 990px (default 1)
 * data-slides-tablet   slides per view from 750px (defaults to desktop value, capped at 2)
 * data-slides-mobile   slides per view below 750px (default 1)
 * data-slide-min-width minimum slide width in px. Switches the element to "auto
 *                      mode": slides per view come from the measured container
 *                      width instead of viewport breakpoints, so a carousel
 *                      sitting in half a column sizes itself correctly and
 *                      follows continuous resizes and browser zoom.
 * data-slides-max      upper bound for the computed slides per view (auto mode)
 * data-gap             gap between slides in px (default 16)
 * data-loop            'true' to loop
 * data-autoplay        delay in ms, 0/absent to disable
 * data-zoom            'true' to enable pinch/double-tap zoom (slides must
 *                      wrap their image in .swiper-zoom-container)
 *
 * The data-a11y-* attributes carry the translated strings Swiper's own A11y
 * module would otherwise write in English (see snippets/carousel.liquid), and
 * an autoplaying carousel gets a pause control wired here (WCAG 2.2.2).
 *
 * Public API: goTo(index) moves the carousel to a slide, and works both before
 * and after Swiper has taken over. The product page uses it to follow the
 * selected variant (see blocks/_product-variant-picker.liquid).
 */
(() => {
  if (customElements.get('citrus-carousel')) return;

  class CitrusCarousel extends HTMLElement {
    connectedCallback() {
      // the script is deferred, so on first upgrade the slides are already
      // parsed and measurable: size them synchronously, before the first paint
      this.syncPreInit();
      // theme editor can re-attach the element; defer so children exist
      requestAnimationFrame(() => this.init());
    }

    disconnectedCallback() {
      this.destroy();
    }

    get autoMode() {
      return Boolean(this.dataset.slideMinWidth);
    }

    /** Slides that fit the measured container width, clamped and capped. */
    slidesFromWidth() {
      const min = Number(this.dataset.slideMinWidth);
      const gap = Number(this.dataset.gap || 16);
      const max = Number(this.dataset.slidesMax || 0);
      const container = this.querySelector('.swiper') || this;
      const width = container.clientWidth;
      if (!width || !min) return 1;

      // +gap on both sides: n slides need (n - 1) gaps, not n
      let slides = Math.floor((width + gap) / (min + gap));
      slides = Math.max(1, slides);
      if (max > 0) slides = Math.min(slides, max);

      // never leave a trailing gap when there are fewer slides than fit
      const count = this.querySelectorAll('.swiper-slide').length;
      return count > 0 ? Math.min(slides, count) : slides;
    }

    /** Feeds the CSS pre-init guard so nothing shifts when Swiper takes over. */
    syncPreInit() {
      if (!this.autoMode) return;
      this.style.setProperty('--slides-pre', this.slidesFromWidth());
    }

    /** Watches the element itself, so zoom and column resizes are covered. */
    observeWidth() {
      if (!this.autoMode || typeof ResizeObserver === 'undefined') return;

      this.observer = new ResizeObserver(() => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (!this.swiper) return;
          const slides = this.slidesFromWidth();
          if (slides === this.swiper.params.slidesPerView) return;
          this.swiper.params.slidesPerView = slides;
          this.swiper.update();
        });
      });
      this.observer.observe(this.querySelector('.swiper') || this);
    }

    init() {
      if (this.swiper || typeof window.Swiper === 'undefined') return;
      const container = this.querySelector('.swiper');
      if (!container) return;

      const desktop = Number(this.dataset.slidesDesktop || 1);
      const mobile = Number(this.dataset.slidesMobile || 1);
      const tablet = Number(this.dataset.slidesTablet || Math.min(desktop, 2));
      const gap = Number(this.dataset.gap || 16);
      const autoplayDelay = Number(this.dataset.autoplay || 0);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const options = {
        slidesPerView: mobile,
        spaceBetween: gap,
        loop: this.dataset.loop === 'true',
        watchOverflow: true,
        breakpoints: {
          750: { slidesPerView: tablet },
          990: { slidesPerView: desktop },
        },
        keyboard: { enabled: true, onlyInViewport: true },
      };

      if (this.autoMode) {
        // viewport breakpoints say nothing about how wide this element is
        delete options.breakpoints;
        options.slidesPerView = this.slidesFromWidth();
      }

      // Swiper's A11y module rewrites the aria-labels of the controls it owns at
      // init, so the translations have to be handed to it rather than only put
      // in the markup. `@@` and `%%` are the Liquid-safe stand-ins for Swiper's
      // own {{index}} / {{slidesLength}} placeholders.
      options.a11y = this.a11yOptions();

      const prev = this.querySelector('.carousel__button--prev');
      const next = this.querySelector('.carousel__button--next');
      if (prev && next) {
        options.navigation = { prevEl: prev, nextEl: next };
      }

      const pagination = this.querySelector('.carousel__pagination');
      if (pagination) {
        options.pagination = { el: pagination, clickable: true };
      }

      if (this.dataset.zoom === 'true') {
        // toggle on single click/tap instead of Swiper's double-tap default
        options.zoom = { maxRatio: 2.5, toggle: false };
      }

      if (autoplayDelay > 0 && !reducedMotion) {
        options.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: true,
          pauseOnMouseEnter: true,
        };
      }

      // a goTo() that landed before Swiper existed: apply it as the starting
      // position instead of animating there right after init
      if (this.pendingSlide) {
        options.initialSlide = this.pendingSlide;
        this.pendingSlide = null;
      }

      this.swiper = new window.Swiper(container, options);
      this.observeWidth();
      this.bindPause(Boolean(options.autoplay));

      if (options.zoom) {
        this.swiper.on('click', (swiper) => swiper.zoom.toggle());
      }
    }

    /** Only the strings actually present are passed, so a missing one keeps
     *  Swiper's default instead of blanking the label. */
    a11yOptions() {
      const data = this.dataset;
      const messages = {
        prevSlideMessage: data.a11yPrev,
        nextSlideMessage: data.a11yNext,
        firstSlideMessage: data.a11yFirst,
        lastSlideMessage: data.a11yLast,
        itemRoleDescriptionMessage: data.a11ySlide,
        paginationBulletMessage: (data.a11yBullet || '').replace('@@', '{{index}}'),
        slideLabelMessage: (data.a11ySlideLabel || '')
          .replace('@@', '{{index}}')
          .replace('%%', '{{slidesLength}}'),
      };

      return Object.fromEntries(Object.entries(messages).filter(([, value]) => value));
    }

    /**
     * Pause/resume control for an autoplaying carousel. `pauseOnMouseEnter` only
     * serves the mouse, so WCAG 2.2.2 needs this button; and because
     * `disableOnInteraction` stops the rotation behind our back after a swipe,
     * the button follows Swiper's own events instead of tracking its own state.
     */
    bindPause(hasAutoplay) {
      const button = this.querySelector('.carousel__pause');
      if (!button) return;

      // reduced motion, or nothing to rotate: an inert control is worse than none
      if (!hasAutoplay || this.querySelectorAll('.swiper-slide').length < 2) {
        button.remove();
        return;
      }

      const setState = (paused) => {
        button.setAttribute('aria-pressed', String(paused));
        button.setAttribute('aria-label', paused ? button.dataset.playLabel : button.dataset.pauseLabel);
      };

      // the element survives a theme-editor re-init, the listener must not stack
      if (!this.pauseBound) {
        this.pauseBound = true;
        button.addEventListener('click', () => {
          if (button.getAttribute('aria-pressed') === 'true') this.swiper?.autoplay?.start();
          else this.swiper?.autoplay?.stop();
        });
      }

      this.swiper.on('autoplayStop', () => setState(true));
      this.swiper.on('autoplayStart', () => setState(false));
      setState(false);
    }

    /**
     * Moves the carousel to a slide. When it is called before Swiper exists the
     * position is remembered and applied by init() as the initial slide, so a
     * deep link lands on the right slide without an animated jump. Zoom is
     * reset, so the new slide never inherits the previous one's magnification.
     */
    goTo(index) {
      if (index < 0) return;
      if (!this.swiper) {
        this.pendingSlide = index;
        return;
      }
      this.swiper.zoom?.out();
      this.swiper.slideTo(index);
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
      if (this.swiper) {
        this.swiper.destroy(true, false);
        this.swiper = null;
      }
    }
  }

  customElements.define('citrus-carousel', CitrusCarousel);

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('citrus-carousel').forEach((el) => {
      el.destroy();
      el.init();
    });
  });
})();
